/**
 * Public route — Rendering Provider's secure response page.
 * Hit via the link in the secure email. Requires the 6-digit secondary code.
 * No auth required (token-based).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, Check, X, Loader2, Mail, AlertTriangle, Stethoscope,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProviderRespond() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);

  // Form state
  const [code, setCode] = useState("");
  const [accepting, setAccepting] = useState(null);
  const [apptDate, setApptDate] = useState("");
  const [comments, setComments] = useState("");
  const [fax, setFax] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/rendering-provider/secure-link/${token}`)
      .then(r => setMeta(r.data))
      .catch(e => setError(e.response?.data?.detail || "Link is invalid or expired"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!code) { toast.error("Enter your 6-digit verification code"); return; }
    if (accepting === null) { toast.error("Indicate whether you can accept this patient"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/rendering-provider/secure-link/${token}/respond`, {
        code, accepting,
        earliest_appt_date: apptDate,
        comments,
        updated_fax: fax,
        updated_phone: phone,
        updated_email: email,
      });
      if (r.data.ok) setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-6"
      data-testid="provider-respond-page">
      <Card className="max-w-2xl w-full bg-white shadow-xl">
        <CardHeader className="bg-gradient-to-r from-[#0066a1] to-[#0080c8] text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5" /> Priorath — Provider Response Portal
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {loading && <div className="py-16 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>}
          {error && (
            <div className="py-10 text-center">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-gray-900">{error}</p>
            </div>
          )}
          {meta && !submitted && (
            <>
              <div className="mb-4">
                <p className="text-sm text-gray-600">You have been asked to confirm whether you can accept a new patient referral. Please verify your identity to proceed.</p>
              </div>
              <div className="bg-blue-50 rounded p-3 mb-4 text-sm">
                <div><span className="font-semibold">Patient:</span> {meta.patient_name || "—"}</div>
                <div><span className="font-semibold">Service requested:</span> {meta.service_type || "—"}</div>
                <div><span className="font-semibold">Rendering Provider:</span> {meta.provider_name || "—"}</div>
                <div><span className="font-semibold">Link expires:</span> {meta.expires_at ? new Date(meta.expires_at).toLocaleString() : "—"}</div>
              </div>

              {meta.already_responded ? (
                <div className="py-10 text-center text-emerald-600 font-semibold">
                  <Check className="w-10 h-10 mx-auto mb-2" />
                  This request has already been responded to. Thank you!
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-semibold">6-digit verification code (from your email)</Label>
                    <Input value={code} onChange={e => setCode(e.target.value)} maxLength={6}
                      placeholder="123456" className="mt-1 font-mono text-lg tracking-widest text-center"
                      data-testid="verify-code-input" />
                  </div>

                  <div>
                    <Label className="text-xs font-semibold">Can you accept this patient?</Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <button
                        onClick={() => setAccepting(true)}
                        className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                          accepting === true ? "border-emerald-500 bg-emerald-50" : "border-gray-200 hover:border-emerald-300"
                        }`}
                        data-testid="accept-yes-btn"
                      >
                        <Check className="w-5 h-5 text-emerald-600" />
                        <span className="font-semibold">Yes, accept</span>
                      </button>
                      <button
                        onClick={() => setAccepting(false)}
                        className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                          accepting === false ? "border-red-500 bg-red-50" : "border-gray-200 hover:border-red-300"
                        }`}
                        data-testid="accept-no-btn"
                      >
                        <X className="w-5 h-5 text-red-600" />
                        <span className="font-semibold">No, decline</span>
                      </button>
                    </div>
                  </div>

                  {accepting === true && (
                    <div>
                      <Label className="text-xs font-semibold">Earliest available appointment</Label>
                      <Input type="date" value={apptDate} onChange={e => setApptDate(e.target.value)} className="mt-1" data-testid="appt-date-input" />
                    </div>
                  )}

                  <div>
                    <Label className="text-xs font-semibold">Comments (optional)</Label>
                    <Textarea value={comments} onChange={e => setComments(e.target.value)} rows={3} className="mt-1" data-testid="comments-textarea" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div><Label className="text-xs">Updated Fax</Label>
                      <Input value={fax} onChange={e => setFax(e.target.value)} className="mt-1" /></div>
                    <div><Label className="text-xs">Updated Phone</Label>
                      <Input value={phone} onChange={e => setPhone(e.target.value)} className="mt-1" /></div>
                    <div><Label className="text-xs">Scheduling Email</Label>
                      <Input value={email} onChange={e => setEmail(e.target.value)} className="mt-1" /></div>
                  </div>

                  <Button onClick={submit} disabled={submitting} className="w-full bg-[#0066a1] hover:bg-[#005080] text-white" data-testid="submit-response-btn">
                    {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                    Submit Response
                  </Button>
                </div>
              )}
            </>
          )}
          {submitted && (
            <div className="py-10 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                <Check className="w-10 h-10 text-emerald-600" />
              </div>
              <p className="text-lg font-bold text-gray-900">Response submitted</p>
              <p className="text-sm text-gray-600 mt-1">Thank you. The requesting practice has been notified.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
